import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { jt, t } from "ttag";
import { z } from "zod";

import { ProtectedInformation } from "@/assets/onboarding/protected-information";
import { EXTERNAL_LINK } from "@/config/external-links";
import { EVENT_PREFIX } from "@/features/analytics/models";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackEmailPhonePageViewed } from "@/features/analytics/trackers/email-phone-page-viewed";
import { useCreateUser } from "@/features/onboarding/hooks";
import { contactInfoSchema } from "@/features/onboarding/models";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { productsQueriesStore } from "@/features/products/api";
import { LogoLoader, SmsMarketingConsentField } from "@/shared/components";
import { CATEGORY_SLUG } from "@/shared/domains";
import {
  Checkbox,
  Form,
  Input,
  LetterIcon,
  PhoneInput,
  SectionHeader,
  Text,
} from "@/shared/ui";
import { formatDOB, handleAxiosFieldErrors } from "@/shared/utils";
import { QuestionNavigation, QuestionStep } from "../components";
import { QUESTIONNAIRE_ROUTE } from "../router/constants";
import { useQuestionnaireStore } from "../stores";

const schema = contactInfoSchema
  .pick({
    email: true,
    phoneNumber: true,
    smsMarketingConsent: true,
    emailMarketingConsent: true,
  })
  .extend({
    termsAndConditionsAccepted: z.boolean().refine((value) => value === true, {
      error: () => t`You must agree to the Terms & Conditions to continue`,
    }),
  });

export const ContactDetails = () => {
  const navigate = useNavigate();
  const { analytics } = useCustomerIoContext();

  const personalDetails = useOnboardingStore((state) => state.personalDetails);
  const contactDetails = useOnboardingStore((state) => state.contactDetails);
  const setContactDetails = useOnboardingStore(
    (state) => state.setContactDetails,
  );

  const heightFeet = useQuestionnaireStore((store) => store.heightFeet);
  const heightInches = useQuestionnaireStore((store) => store.heightInches);
  const initialWeight = useQuestionnaireStore((store) => store.initialWeight);
  const gender = useQuestionnaireStore((store) => store.gender);
  const dateOfBirth = useQuestionnaireStore((store) => store.dateOfBirth);
  const state = useQuestionnaireStore((store) => store.state);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      email: contactDetails?.email,
      phoneNumber: contactDetails?.phoneNumber,
      smsMarketingConsent: contactDetails?.smsMarketingConsent ?? false,
      emailMarketingConsent: contactDetails?.emailMarketingConsent ?? false,
      termsAndConditionsAccepted: false,
    },
  });

  const createUser = useCreateUser();
  const queryClient = useQueryClient();

  useEffect(() => {
    trackEmailPhonePageViewed({
      customerIoAnalytics: analytics,
      eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!personalDetails) {
    return <Navigate to={QUESTIONNAIRE_ROUTE.PERSONAL_DETAILS} replace />;
  }

  const handleNext = form.handleSubmit(
    ({
      termsAndConditionsAccepted: _termsAndConditionsAccepted,
      ...values
    }) => {
      // Prefetches product.tsx's products query in parallel with the user
      // creation request, so it's already cached by the time we navigate
      // there and the page never shows its own loading spinner. Awaited
      // before navigating (below) so a slow prefetch can't lose the race.
      const productsPrefetch = queryClient
        .prefetchQuery(
          productsQueriesStore.products.category._ctx.products({
            categorySlug: CATEGORY_SLUG.WEIGHT_LOSS,
          }),
        )
        .catch(() => undefined);

      createUser.mutate(
        {
          ...personalDetails,
          ...values,
          heightFeet,
          heightInches,
          weightInPounds: initialWeight,
          gender,
          dateOfBirth: dateOfBirth ? formatDOB(dateOfBirth) : undefined,
          eligibilityAnswerIds: [],
        },
        {
          onSuccess: (data) => {
            setContactDetails({
              billing: {
                address: "",
                apartmentNumber: "",
                city: "",
                zipCode: "",
              },
              shipping: {
                address: "",
                apartmentNumber: "",
                city: "",
                zipCode: "",
              },
              state: state ?? "",
              email: data.email,
              code: data.code,
              ehrId: data.ehrId,
              phoneNumber: values.phoneNumber,
              smsMarketingConsent: values.smsMarketingConsent ?? false,
              emailMarketingConsent: values.emailMarketingConsent ?? false,
            });

            void productsPrefetch.then(() =>
              navigate(QUESTIONNAIRE_ROUTE.PRODUCT),
            );
          },
          onError: (error) => handleAxiosFieldErrors(error, form.setError),
        },
      );
    },
  );

  const termsAndConditionsLink = (
    <Link
      key="terms-and-conditions-link"
      to={EXTERNAL_LINK.TERMS_AND_CONDITIONS}
      className="underline"
      target="_blank"
      rel="noreferrer noopener"
    >
      {t`Terms & Conditions`}
    </Link>
  );

  return (
    <Form.Provider {...form}>
      <QuestionStep
        onSubmit={handleNext}
        footer={
          <QuestionNavigation
            disabled={createUser.isPending}
            onBack={() => navigate(QUESTIONNAIRE_ROUTE.PERSONAL_DETAILS)}
          />
        }
      >
        <LogoLoader show={createUser.isPending} fullscreen />

        <SectionHeader
          title={t`Enter your contact details`}
          description={t`We respect your privacy - your details are used only for essential physician communication`}
        />

        <div className="flex w-full flex-col gap-5 sm:max-w-xl">
          <Form.Field
            control={form.control}
            name="email"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label aria-required>{t`Email`}</Form.Label>
                <Form.Control>
                  <Input.Root
                    {...form.register("email")}
                    type="email"
                    placeholder={t`Enter your email`}
                  >
                    <Input.Slot>
                      <LetterIcon />
                    </Input.Slot>
                  </Input.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />

          <Form.Field
            control={form.control}
            name="phoneNumber"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label aria-required>{t`Phone number`}</Form.Label>
                <Form.Control>
                  <PhoneInput {...form.register("phoneNumber")} />
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />

          <div className="flex flex-col gap-3">
            <Form.Field
              control={form.control}
              name="termsAndConditionsAccepted"
              render={({ field: { value, onChange, ...rest } }) => (
                <Form.Item>
                  <Form.Control>
                    <Checkbox.Item
                      {...rest}
                      checked={value}
                      onCheckedChange={onChange}
                    >
                      <Checkbox.Label className="translate-y-1 text-xs">
                        {jt`I agree to the ${termsAndConditionsLink}.`}
                      </Checkbox.Label>
                    </Checkbox.Item>
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <Form.Field
              control={form.control}
              name="emailMarketingConsent"
              render={({ field: { value, onChange, ...rest } }) => (
                <Form.Item>
                  <Form.Control>
                    <Checkbox.Item
                      {...rest}
                      checked={value}
                      onCheckedChange={onChange}
                    >
                      <Checkbox.Label className="translate-y-1 text-xs">
                        {t`I consent to receive emails about my care and updates.`}
                      </Checkbox.Label>
                    </Checkbox.Item>
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <SmsMarketingConsentField control={form.control} />
          </div>

          <Text size="small" color="light-neutral">
            {t`We'll only use your number to text order updates and important information about your treatments.`}
          </Text>

          <ProtectedInformation />
        </div>
      </QuestionStep>
    </Form.Provider>
  );
};
